import React, { useState } from 'react';
import CarPreview from './CarPreview';
import ViewSelector from './ViewSelector';
import PriceBreakdownModal from './PriceBreakdownModal';
import { useCart } from '../../context/CartContext';
import CartSuccessModal from './CartSuccessModal';
import { loadStripe } from '@stripe/stripe-js';
import { Plus, Minus } from 'lucide-react';
import { formatPrice } from '../../utils/formatPrice';
import { generateDesignPreview, generateAllViewsPreviews } from '../../utils/designPreview';
import { logger } from '../../utils/logger';
import { toast } from 'react-hot-toast';
import { Button } from '../ui/button';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// Loading overlay component with Shadcn styling
const CaptureLoadingOverlay = () => (
  <div className="fixed inset-0 bg-white z-50 flex items-center justify-center">
    <div className="bg-card border border-border rounded-lg shadow-lg p-8 max-w-sm">
      <div className="flex flex-col items-center space-y-4">
        <div className="relative">
          <div className="h-16 w-16 rounded-full border-4 border-muted"></div>
          <div className="absolute top-0 left-0 h-16 w-16 rounded-full border-4 border-primary border-t-transparent animate-spin"></div>
        </div>
        <div className="text-center space-y-2">
          <p className="text-lg font-semibold text-foreground">Bearbetar design</p>
          <p className="text-sm text-muted-foreground">Läser av alla vyer och lägger till i varukorgen...</p>
        </div>
      </div>
    </div>
  </div>
);

const OrderSummary = ({ 
  selectedCar, 
  selectedColor,
  helfoliering = false,
  helfolieringFee = 10000,
  selectedView,
  placedGraphics, 
  price,
  onSubmitOrder,
  carDimensions,
  setSelectedView,
  onComplete,
  onDuplicateDesign,
  isEditMode,
  onBackToDesign
}) => {
  const [quantity, setQuantity] = useState(1);
  const [needsInstallation, setNeedsInstallation] = useState(false);
  const [comments, setComments] = useState('');
  const [isPriceModalOpen, setIsPriceModalOpen] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false); // New state for capture process
  const { addToCart, updateCartItem } = useCart();

  const installationCost = needsInstallation ? 2500 : 0;
  const totalCost = (price * quantity) + installationCost;

  // Calculate total number of decals across all cars
  const dekalsPerCar = Object.values(placedGraphics).flat().length;
  const totalDekals = dekalsPerCar * quantity;

  const handleIncrement = () => {
    setQuantity(prev => prev + 1);
  };

  const handleDecrement = () => {
    setQuantity(prev => Math.max(1, prev - 1));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Use the state defined at the component level
    setIsSubmitting(true);
    setIsCapturing(true); // Show the capture overlay
    
    try {
      // Generate previews for all views
      const previewImages = await generateAllViewsPreviews(selectedView, setSelectedView);
      
      if (!previewImages || Object.keys(previewImages).length === 0) {
        logger.error('Failed to generate preview images');
        return;
      }
      
      const orderDetails = {
        car: selectedCar,
        quantity,
        needsInstallation,
        comments,
        pricePerUnit: price,
        totalCost,
        design: placedGraphics,
        selectedColor: selectedColor,
        helfoliering: !!helfoliering,
        previewImages, // Include all preview images
        previewImage: previewImages[selectedView] // Keep the current view as the main preview for backward compatibility
      };

      if (isEditMode) {
        onSubmitOrder(orderDetails);
      } else {
        addToCart(orderDetails);
      }
      setIsSuccessModalOpen(true);
    } catch (error) {
      logger.error('Error submitting order:', error);
      // Show error toast
      toast.error('Ett fel uppstod. Försök igen.');
    } finally {
      setIsSubmitting(false);
      setIsCapturing(false); // Hide the capture overlay
    }
  };

  return (
    <main className="flex flex-col md:flex-row car-preview-wrapper bg-secondary/50 h-[calc(100vh-3.5rem)] overflow-hidden">
      
      <div className="flex-1 min-w-0 relative">
        <div className="w-full h-full max-w-full overflow-hidden">
        {isCapturing && <CaptureLoadingOverlay />}

          <CarPreview 
            selectedColor={selectedColor}
            selectedView={selectedView}
            placedGraphics={placedGraphics}
            carDimensions={selectedCar?.dimensions || carDimensions}
            selectedCarModel={selectedCar?.id}
            carModelData={selectedCar}
            readOnly={true}
            onBackToCarSelection={onBackToDesign}
          />
        </div>
        <ViewSelector 
          selectedView={selectedView}
          setSelectedView={setSelectedView}
        />
      </div>

      {/* Order Summary Sidebar */}
      <aside className="w-64 sidebar-order-summary bg-white border-l border-gray-200 flex flex-col h-full">

        <form onSubmit={handleSubmit} className="flex-1 flex flex-col">
        {/* <div class="box-generic p-4">
          <h2 class="text-sm font-medium mb-4">Placering av dekaler</h2>

            <label className="flex items-center space-x-3 switch">
              <input
                type="checkbox"
                checked={needsInstallation}
                onChange={(e) => setNeedsInstallation(e.target.checked)}
                className="form-checkbox h-5 w-5 text-purple-600"
              />
              <span className="slider"></span>
              <span className="input-wrapper-slider">
                <span className="text-sm">Jag vill fästa själv <br /> <span className="input-wrapper-slider-price">0 kr tillkommer</span></span>
                <span className="text-sm">Jag vill ha hjälp med att fästa dekaler <br /> <span className="input-wrapper-slider-price">2 500 kr tillkommer</span></span>
              </span>
            </label>
          </div> */}

          <div className="p-4 space-y-4">
            <div>
              <h3 className="text-sm font-medium mb-1">Antal bilar</h3>
              <p className="text-sm text-muted-foreground">
                Har du flera bilar av samma modell som ska ha samma dekaler?
              </p>
            </div>
            <div className="flex items-center justify-center space-x-4">
              <Button
                onClick={handleDecrement}
                variant="outline"
                size="icon"
                type="button"
                className="h-10 w-10"
              >
                <Minus size={16} />
              </Button>
              <div className="flex-1 text-center text-3xl font-semibold">
                {quantity}
              </div>
              <Button
                onClick={handleIncrement}
                variant="outline"
                size="icon"
                type="button"
                className="h-10 w-10"
              >
                <Plus size={16} />
              </Button>
            </div>
          </div>

          {/* <div class="box-generic p-4">
            <label className="block text-sm font-medium mb-4">
              Önskemål
            </label>
            <textarea
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              className="bg-gray-700 px-4 py-2 rounded-lg w-full h-32 resize-none text-sm"
              placeholder="Skriv eventuella önskemål eller frågor här..."
            />
          </div> */}

          {/* Spacer to push Din design section to bottom */}
          <div className="flex-1"></div>

          <div className="p-4 space-y-3">
            <h3 className="text-lg font-semibold">Din design</h3>
            
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Bilmodell</span>
                <span className="font-medium">{selectedCar?.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Antal dekaler per bil</span>
                <span className="font-medium">{dekalsPerCar} st</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Totalt antal dekaler</span>
                <span className="font-medium">{totalDekals} st</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Pris per bil</span>
                <span className="font-medium">{formatPrice(price)} kr</span>
              </div>
              {helfoliering && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Helfoliering</span>
                  <span className="font-medium">inkl. i pris per bil (+ {formatPrice(helfolieringFee)} kr)</span>
                </div>
              )}
            </div>
            
            <div className="flex justify-between text-sm pt-2 border-t border-border">
              <span className="text-muted-foreground">Antal bilar</span>
              <span className="font-medium">{quantity} st</span>
            </div>
            
            {needsInstallation && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Montering</span>
                <span className="font-medium">{formatPrice(installationCost)} kr</span>
              </div>
            )}
            
            <div className="border-t border-border pt-3 space-y-1">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">Totalt inkl moms</span>
                <span className="text-2xl font-bold">{formatPrice(totalCost)} kr</span>
              </div>
            </div>

            <Button 
              type="submit"
              disabled={isSubmitting}
              className="w-full"
              size="lg"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Bearbetar...
                </span>
              ) : (
                isEditMode ? 'Uppdatera' : 'Lägg i varukorgen'
              )}
            </Button>
          </div>
        </form>
      </aside>

      <PriceBreakdownModal 
        isOpen={isPriceModalOpen}
        onClose={() => setIsPriceModalOpen(false)}
        placedGraphics={placedGraphics}
        helfoliering={helfoliering}
        helfolieringFee={helfolieringFee}
      />

      <CartSuccessModal
        isOpen={isSuccessModalOpen}
        onClose={() => setIsSuccessModalOpen(false)}
        onDesignAnother={onComplete}
        onDuplicateDesign={onDuplicateDesign}
      />
    </main>
  );
};

export default OrderSummary;